import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles, X } from "lucide-react";
import api from "@/lib/apiClient";

// v6: pantalla de espera de la búsqueda. Pasos y contadores REALES del backend (job.progress),
// tiempo estimado según las búsquedas anteriores del usuario y primeras oportunidades encontradas.
const STEPS = [
  { title: "Buscando en Instagram", sub: (p) => (p.found ? `${p.found} perfiles encontrados` : "") },
  { title: "Filtrando", sub: (p) => (p.discarded != null ? `${p.discarded} descartados por país, actividad o repetidos` : "") },
  { title: "Analizando a fondo", sub: (p) => (!p.to_review ? "" : p.reviewed ? `${Math.min(p.reviewed, p.to_review)} de ${p.to_review} perfiles revisados` : `Leyendo ${p.to_review} perfiles`) },
  { title: "Valorando el encaje", sub: (p) => (p.fit != null ? `${p.fit} ${p.fit === 1 ? "encaja" : "encajan"} contigo` : "") },
  { title: "Escribiendo tus mensajes", sub: (p) => (p.messages_total ? `${p.messages_done || 0} de ${p.messages_total} mensajes listos` : "") },
];
const WHY = [
  "Buscamos con tus palabras y con variantes por país y ciudad.",
  "Solo seguimos con perfiles de tu mercado, activos y que no te hayamos mostrado antes.",
  "Leemos la bio, la categoría y sus últimas publicaciones para entender a qué se dedica.",
  "Valoramos si encaja con tu negocio y por qué merece la pena contactar.",
  "Cada mensaje empieza con algo real de su perfil: nada de plantillas.",
];

export function progressPct(p = {}) {
  const step = Math.max(1, Math.min(5, p.step || 1));
  let inner = 0.5;
  if (step === 3 && p.to_review) inner = Math.min(1, (p.reviewed || 0) / p.to_review);
  if (step === 5 && p.messages_total) inner = Math.min(1, (p.messages_done || 0) / p.messages_total);
  const byStep = ((step - 1) + inner) / 5;
  const byTime = p.typical_s && p.elapsed_s != null ? Math.min(0.95, p.elapsed_s / p.typical_s) : 0;
  return Math.round(Math.max(byStep, Math.min(byTime, step / 5)) * 100);
}

export function etaText(p = {}) {
  if (!p.typical_s || p.elapsed_s == null) return "Unos minutos";
  const left = p.typical_s - p.elapsed_s;
  if (left > 90) return `Unos ${Math.round(left / 60)} minutos`;
  if (left > 40) return "Menos de 2 minutos";
  if (left > 0) return "Menos de 1 minuto";
  return "Casi listo";
}

const OBJ = { cliente: "clientes", socio: "socios", afiliado: "afiliados", colaborador: "colaboradores", recomendador: "recomendadores", seguidor: "seguidores", equipo: "equipo" };

export default function SearchProgress({ job, onCancel, pendingCount = 0, onShowPending, statusLabel = "" }) {
  const p = job?.progress || {};
  const step = Math.max(1, Math.min(5, p.step || 1));
  const pct = progressPct(p);
  const early = p.early || [];
  const [today, setToday] = useState(null);

  useEffect(() => { api.get("/prospects/today").then((r) => setToday((r.data || []).length)).catch(() => {}); }, []);

  return (
    <div className="space-y-6" data-testid="opp-progress">
      <section className="ca-card ca-card-gold p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="ca-eyebrow">Búsqueda en curso</p>
            <p className="mt-1 text-lg font-bold text-brand-ink" data-testid="opp-progress-title">
              {job.avatar_name || "Tu búsqueda"}{OBJ[job.objective] ? ` · para ${OBJ[job.objective]}` : ""}
            </p>
            <p className="text-xs text-brand-muted"><span data-testid="opp-progress-status">{statusLabel}</span>{job.route_label ? ` · Ruta: ${job.route_label}` : ""}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-brand-ink" data-testid="opp-progress-eta">{etaText(p)}</p>
            {p.typical_s ? <p className="text-[11px] text-brand-muted">tus búsquedas suelen tardar unos {Math.max(1, Math.round(p.typical_s / 60))} min</p> : null}
          </div>
        </div>
        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-brand-line/60">
          <div className="h-full rounded-full bg-brand-accent transition-all duration-700" style={{ width: `${pct}%` }} data-testid="opp-progress-bar" />
        </div>
        <ol className="mt-4 space-y-1.5">
          {STEPS.map((s, i) => {
            const n = i + 1;
            const st = n < step ? "done" : n === step ? "now" : "next";
            const sub = st !== "next" ? s.sub(p) : "";
            return (
              <li key={s.title} data-testid={`opp-step-${n}`} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${st === "now" ? "bg-brand-panel2/60" : ""}`}>
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold ${st === "done" ? "border-brand-pos/40 bg-brand-pos/10 text-brand-pos" : st === "now" ? "border-brand-accent text-brand-accent" : "border-brand-line text-brand-muted"}`}>
                  {st === "done" ? <Check size={14} /> : st === "now" ? <Loader2 size={14} className="animate-spin" /> : n}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block text-sm font-semibold ${st === "next" ? "text-brand-muted" : "text-brand-ink"}`}>{s.title}</span>
                  {sub && <span className="block text-[11px] text-brand-muted">{sub}</span>}
                </span>
                {st === "now" && <span className="font-mono text-[11px] text-brand-accent">en curso</span>}
              </li>
            );
          })}
        </ol>
        {p.autofix?.texto && (
          <p className="mt-4 rounded-lg bg-brand-accent/[0.08] px-3 py-2 text-xs text-brand-ink" data-testid="opp-progress-autofix">
            <span className="font-semibold">Aún no habíamos encontrado perfiles que encajen.</span> {p.autofix.texto}
          </p>
        )}
        <p className="mt-4 flex items-start gap-2 rounded-lg border border-dashed border-brand-accent/40 px-3 py-2 text-xs text-brand-ink" data-testid="opp-progress-why">
          <Sparkles size={13} className="mt-0.5 shrink-0 text-brand-accent" /> {WHY[step - 1]}
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-brand-muted">
          <span>Puedes seguir usando CaptAcción: la búsqueda sigue en segundo plano y verás el aviso arriba al terminar.</span>
          {job.cancelable && (
            <button type="button" onClick={onCancel} data-testid="opp-cancel-btn" className="inline-flex items-center gap-1 font-semibold underline hover:text-brand-neg">
              <X size={12} /> Cancelar búsqueda
            </button>
          )}
        </div>
      </section>

      <section data-testid="opp-early">
        <p className="mb-2 flex items-center gap-2 text-sm font-bold text-brand-ink">
          Ya tenemos estas {early.length > 0 && <span className="rounded-full bg-brand-accent px-2 text-[11px] text-brand-canvas">{early.length}</span>}
        </p>
        {early.length === 0 ? (
          <p className="text-xs text-brand-muted">Aparecerán aquí en cuanto encontremos las primeras que encajan.</p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {early.map((e, i) => (
                <div key={e.username || i} className="ca-card p-4" data-testid={`opp-early-${i}`}>
                  <div className="flex items-center gap-3">
                    {e.photo_url ? <img src={e.photo_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                      : <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-accent/15 text-xs font-bold text-brand-accent">{(e.name || "?").slice(0, 2).toUpperCase()}</span>}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-brand-ink">{e.name}</p>
                      <p className="truncate text-[11px] text-brand-muted">{[e.location, e.followers ? `${Number(e.followers).toLocaleString("es-ES")} seguidores` : ""].filter(Boolean).join(" · ") || (e.username ? `@${e.username}` : "")}</p>
                    </div>
                  </div>
                  {e.why && <p className="mt-2 line-clamp-2 text-xs text-brand-ink">{e.why}</p>}
                  {e.other_brand && <p className="mt-1 text-[11px] font-semibold text-brand-accent">Trabaja con otra marca: {e.other_brand}</p>}
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-brand-muted">Las verás completas, con su primer mensaje escrito, en cuanto terminemos.</p>
          </>
        )}
      </section>

      <section>
        <p className="mb-2 text-sm font-bold text-brand-ink">Mientras tanto</p>
        <div className="grid gap-3 sm:grid-cols-3" data-testid="opp-meanwhile">
          <div className="ca-card p-4">
            <p className="text-sm font-bold text-brand-ink">{today ? `${today} ${today === 1 ? "acción" : "acciones"} para hoy` : "Qué hacer hoy"}</p>
            <p className="mt-1 text-xs text-brand-muted">{today ? "Tus contactos te esperan: avanza con ellos mientras buscamos." : "Revisa tus próximos pasos con tus contactos."}</p>
            <Link to="/app/hoy" className="mt-2 inline-block text-xs font-semibold text-brand-accent">Ir a Qué hacer hoy →</Link>
          </div>
          {pendingCount > 0 && (
            <div className="ca-card p-4">
              <p className="text-sm font-bold text-brand-ink">{pendingCount} pendientes de revisar</p>
              <p className="mt-1 text-xs text-brand-muted">Oportunidades de búsquedas anteriores que aún no has guardado ni descartado.</p>
              <button type="button" onClick={onShowPending} className="mt-2 text-xs font-semibold text-brand-accent">Revisarlas →</button>
            </div>
          )}
          <div className="ca-card p-4">
            <p className="text-sm font-bold text-brand-ink">Revisa «Mi negocio»</p>
            <p className="mt-1 text-xs text-brand-muted">Cuanto más claro esté tu negocio, mejores mensajes escribe la IA.</p>
            <Link to="/app/brain" className="mt-2 inline-block text-xs font-semibold text-brand-accent">Ir a Mi negocio →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Aviso global en la barra superior: se ve desde cualquier pantalla mientras busca y al terminar.
export function SearchPill() {
  const [job, setJob] = useState(null);
  const [justDone, setJustDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let timer = null;
    let wasActive = false;
    const poll = async () => {
      try {
        const r = await api.get("/opportunities/search/current");
        const j = r.data.job;
        setJob(j);
        if (j && j.active) { wasActive = true; timer = setTimeout(poll, 6000); return; }
        if (wasActive && j && !j.active) { setJustDone(true); window.dispatchEvent(new Event("ca:search-finished")); }
        wasActive = false;
      } catch (e) { timer = setTimeout(poll, 15000); }
    };
    const restart = () => { clearTimeout(timer); setJustDone(false); poll(); };
    poll();
    window.addEventListener("ca:search-started", restart);
    return () => { clearTimeout(timer); window.removeEventListener("ca:search-started", restart); };
  }, []);

  if (!job) return null;
  if (job.active) {
    const p = job.progress || {};
    return (
      <button type="button" onClick={() => navigate("/app/oportunidades")} data-testid="search-pill"
        className="ml-auto inline-flex items-center gap-2 rounded-full border border-brand-accent/50 px-3 py-1.5 text-xs text-brand-ink hover:bg-brand-accent/[0.06]">
        <Loader2 size={13} className="animate-spin text-brand-accent" />
        <span>Buscando · <b className="text-brand-accent">paso {Math.max(1, p.step || 1)} de 5</b></span>
        <span className="h-1 w-16 overflow-hidden rounded-full bg-brand-line/60"><span className="block h-full bg-brand-accent" style={{ width: `${progressPct(p)}%` }} /></span>
      </button>
    );
  }
  if (justDone) {
    const n = job.result_count || 0;
    return (
      <button type="button" onClick={() => { setJustDone(false); navigate("/app/oportunidades"); }} data-testid="search-pill-done"
        className="ml-auto inline-flex items-center gap-2 rounded-full border border-brand-pos/50 px-3 py-1.5 text-xs font-semibold text-brand-pos hover:bg-brand-pos/[0.06]">
        <Check size={13} /> {n ? `Listas: ${n} ${n === 1 ? "oportunidad" : "oportunidades"} →` : "Búsqueda terminada →"}
      </button>
    );
  }
  return null;
}
